import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeaders, useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Radio, Power, RefreshCw, Wifi, WifiOff, Users, AlertTriangle, CheckCircle2, Eye, EyeOff, Zap, HelpCircle, Trash2, KeyRound } from "lucide-react";

/** Master-admin Bridge dashboard:
 *  • Configure central master MT5 (login/server/password)
 *  • Broker preset selector + inline help to find server name
 *  • Test Connection button (validates with MetaApi before committing)
 *  • View streaming status (connected / last event / error)
 *  • Force resync, see list of active followers */
export default function BridgeMaster() {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const [data, setData] = useState(null);
  const [followers, setFollowers] = useState(null);
  const [presets, setPresets] = useState([]);
  const [cfState, setCfState] = useState(null);
  const [form, setForm] = useState({ mt5_login: "", mt5_server: "", mt5_password: "", broker_name: "", nickname: "Velox Master Signal", metaapi_account_id: "" });
  const [busy, setBusy] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [presetKey, setPresetKey] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [mode, setMode] = useState("credentials"); // 'credentials' | 'bind'

  const refresh = async () => {
    try {
      const [a, b, p, c] = await Promise.all([
        axios.get(`${API}/admin/bridge/master`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/bridge/followers`, { headers: getAuthHeaders() }),
        presets.length ? Promise.resolve({ data: { presets } })
                       : axios.get(`${API}/admin/bridge/presets`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/copyfactory/state`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
      ]);
      setData(a.data);
      setFollowers(b.data);
      setCfState(c.data);
      if (!presets.length) setPresets(p.data?.presets || []);
      if (a.data?.mt5_login) {
        setForm(f => ({
          mt5_login: a.data.mt5_login || "",
          mt5_server: a.data.mt5_server || "",
          mt5_password: "",
          broker_name: a.data.broker_name || "",
          nickname: a.data.nickname || "Velox Master Signal",
          metaapi_account_id: a.data.metaapi_account_id || "",
        }));
        if (a.data.bind_mode) setMode("bind");
      }
    } catch (err) { /* ignore */ }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);

  }, []);

  const selectPreset = (key) => {
    setPresetKey(key);
    const p = presets.find(x => x.key === key);
    if (!p) return;
    setForm(f => ({
      ...f,
      broker_name: p.key === "other" ? f.broker_name : p.label,
      mt5_server: p.server_examples?.[0] || f.mt5_server,
    }));
  };

  const test = async () => {
    if (!isMaster) return;
    if (!form.mt5_login.trim() || !form.mt5_server.trim() || !form.mt5_password) {
      toast.error("Fill login, server, and password to test"); return;
    }
    setBusy("test");
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/admin/bridge/test-connection`, {
        mt5_login: form.mt5_login,
        mt5_server: form.mt5_server,
        mt5_password: form.mt5_password,
      }, { headers: getAuthHeaders(), timeout: 120000 });
      setTestResult(r.data);
      if (r.data?.ok) toast.success("Credentials verified ✓");
      else toast.error(r.data?.error || "Connection test failed");
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Test failed";
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally { setBusy(""); }
  };

  const save = async () => {
    if (!isMaster) return;
    if (mode === "bind") {
      if (!form.metaapi_account_id?.trim()) {
        toast.error("Paste an existing MetaApi account ID"); return;
      }
    } else {
      if (!form.mt5_login.trim() || !form.mt5_server.trim() || !form.mt5_password) {
        toast.error("Login, server and password are required"); return;
      }
    }
    setBusy("save");
    try {
      const payload = mode === "bind"
        ? { metaapi_account_id: form.metaapi_account_id.trim(), broker_name: form.broker_name, nickname: form.nickname }
        : { mt5_login: form.mt5_login, mt5_server: form.mt5_server, mt5_password: form.mt5_password, broker_name: form.broker_name, nickname: form.nickname };
      const r = await axios.put(`${API}/admin/bridge/master`, payload, { headers: getAuthHeaders() });
      toast.success(r.data?.message || "Saved");
      setForm(f => ({ ...f, mt5_password: "" }));
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(""); }
  };

  const resync = async () => {
    if (!isMaster) return;
    setBusy("resync");
    try {
      await axios.post(`${API}/admin/bridge/resync`, {}, { headers: getAuthHeaders() });
      toast.success("Reconnecting…");
      setTimeout(refresh, 2000);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const clear = async () => {
    if (!isMaster) return;
    if (!window.confirm(
      "Clear the saved master MT5?\n\nThis will:\n• Wipe the stored login/server/password\n• Disconnect the MetaApi streaming session\n• Try to remove the MetaApi account (so it stops accruing)\n\nFollowers' bridge settings are NOT touched."
    )) return;
    setBusy("clear");
    try {
      const r = await axios.delete(`${API}/admin/bridge/master`, { headers: getAuthHeaders() });
      toast.success(r.data?.message || "Cleared");
      setForm({ mt5_login: "", mt5_server: "", mt5_password: "", broker_name: "", nickname: "Velox Master Signal" });
      setTestResult(null);
      setPresetKey("");
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to clear"); }
    finally { setBusy(""); }
  };

  const retry = async (uid, name) => {
    if (!isMaster) return;
    if (!window.confirm(`Re-provision MT5 for ${name || uid}?\n\nThe stale MetaApi account will be removed. The next master trade will trigger a fresh provisioning attempt with the affiliate's current MT5 credentials.`)) return;
    setBusy(`retry-${uid}`);
    try {
      const r = await axios.post(`${API}/admin/bridge/followers/${uid}/retry-provision`, {}, { headers: getAuthHeaders() });
      toast.success(r.data?.message || "Re-provision queued");
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const syncStrategy = async () => {
    if (!isMaster) return;
    setBusy("cf-sync");
    try {
      const r = await axios.post(`${API}/admin/copyfactory/sync-master-strategy`, {}, { headers: getAuthHeaders() });
      toast.success(r.data?.message || "Strategy synced");
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
    finally { setBusy(""); }
  };

  const resubscribeFollower = async (uid, name) => {
    if (!isMaster) return;
    if (!window.confirm(`Re-subscribe ${name || uid} to the master CopyFactory strategy?`)) return;
    setBusy(`cf-sub-${uid}`);
    try {
      const r = await axios.post(`${API}/admin/copyfactory/resubscribe/${uid}`, {}, { headers: getAuthHeaders() });
      toast.success(r.data?.message || "Subscribed");
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const resyncAllFollowers = async () => {
    if (!isMaster) return;
    if (!window.confirm("Push the latest symbol mapping to ALL existing followers? Use this when non-Exness followers (XM / Vantage / IC etc.) aren't receiving trades.")) return;
    setBusy("cf-resync-all");
    try {
      const r = await axios.post(`${API}/admin/copyfactory/resync-all-followers`, {}, { headers: getAuthHeaders() });
      const { ok_count = 0, fail_count = 0, reprovision_count = 0, results = [] } = r.data || {};
      toast.success(`Resynced ${ok_count} · Re-provisioning ${reprovision_count} · ${fail_count} failed`);
      // Surface non-ok rows (max 4) so the admin can act
      results.filter(x => x.status !== "ok").slice(0, 4).forEach(x => {
        const msg = x.note || x.error || x.status;
        if (x.status === "reprovisioning") {
          toast.message(`${x.email || x.user_id} [${x.account_type}/${x.broker_key || x.broker}]: ${msg}`);
        } else {
          toast.error(`${x.email || x.user_id} [${x.account_type}/${x.broker_key || x.broker}]: ${msg}`);
        }
      });
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Resync failed"); }
    finally { setBusy(""); }
  };

  const streaming = data?.streaming || {};
  const connected = !!streaming.connected;
  const deploy = streaming.deploy_state;
  const conn = streaming.connection_state;
  const activePreset = presets.find(p => p.key === presetKey);

  // Derive a friendly status label for the pill
  let statusLabel = "Disconnected";
  let statusClass = "bg-rose-500/10 border-rose-500/30 text-rose-300";
  if (connected) { statusLabel = "Streaming"; statusClass = "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"; }
  else if (deploy === "DEPLOYING" || conn === "CONNECTING") { statusLabel = "Provisioning…"; statusClass = "bg-amber-500/10 border-amber-500/30 text-amber-200"; }
  else if (deploy === "DEPLOYED" && conn !== "CONNECTED") { statusLabel = "Connecting to broker…"; statusClass = "bg-sky-500/10 border-sky-500/30 text-sky-300"; }

  return (
    <div className="space-y-6" data-testid="bridge-master-section">
      {/* Header / status */}
      <div className="p-5 rounded-xl border border-white/10 bg-[#13131A]/60">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <Radio size={16} className="text-[#7DD3FC]" />
            <div>
              <h3 className="text-sm font-bold text-white">MT5 Bridge — Master Signal</h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                Central MT5 account that drives copy-trading for every paid CENT follower.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {data?.stub_mode && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-[10px] font-semibold text-amber-200" data-testid="bridge-stub-badge">
                <AlertTriangle size={10} /> STUB MODE
              </span>
            )}
            <span data-testid="bridge-stream-status" className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border ${statusClass}`}>
              {connected ? <Wifi size={10} /> : <WifiOff size={10} />}
              {statusLabel}
            </span>
            {data?.mt5_login && isMaster && (
              <button
                data-testid="bridge-resync-btn"
                onClick={resync}
                disabled={busy === "resync"}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold border border-white/10 text-zinc-300 hover:border-white/30 disabled:opacity-40"
              >
                <RefreshCw size={10} className={busy === "resync" ? "animate-spin" : ""} /> Resync
              </button>
            )}
          </div>
        </div>
        {streaming.last_error && (
          <p data-testid="bridge-last-error" className="mt-3 p-2.5 rounded-md bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-300">
            <AlertTriangle size={10} className="inline mr-1" /> {streaming.last_error}
            {String(streaming.last_error).toLowerCase().includes("top up") && (
              <span className="block mt-1 text-rose-200/80">
                Top up your MetaApi balance at <a className="underline" href="https://app.metaapi.cloud/billing" target="_blank" rel="noreferrer">app.metaapi.cloud/billing</a> and click Resync.
              </span>
            )}
          </p>
        )}
        {(deploy || conn) && !streaming.last_error && (
          <div className="mt-2 flex items-center gap-3 text-[10px] text-zinc-500">
            <span>Deploy: <span className="font-mono text-zinc-300">{deploy || "—"}</span></span>
            <span>·</span>
            <span>Broker: <span className="font-mono text-zinc-300">{conn || "—"}</span></span>
            {streaming.last_event_at && <><span>·</span><span>Last event: <span className="font-mono text-zinc-300">{streaming.last_event_at.slice(0, 16)}</span></span></>}
          </div>
        )}
      </div>

      {/* CopyFactory strategy panel */}
      <div className="p-5 rounded-xl border border-white/10 bg-[#13131A]/60" data-testid="copyfactory-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2.5">
            <Zap size={14} className="text-[#FBBF24]" />
            <div>
              <h4 className="text-xs font-bold text-white">CopyFactory — &quot;Velox Standard&quot; Strategy</h4>
              <p className="text-[10px] text-zinc-500">Master strategy that auto-mirrors trades to every paid CENT follower. SL/TP copied · Balance-scaled · 0% commission.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {cfState?.strategy?.id ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[10px] font-semibold text-emerald-300" data-testid="cf-strategy-status">
                <CheckCircle2 size={10} /> Strategy <span className="font-mono">{cfState.strategy.id}</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-[10px] font-semibold text-amber-200" data-testid="cf-strategy-status">
                <AlertTriangle size={10} /> Not synced
              </span>
            )}
            <span data-testid="cf-subscriber-count" className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border border-white/10 bg-zinc-950/40 text-zinc-300">
              <Users size={10} /> {cfState?.subscriber_count || 0} subscribers
            </span>
            {isMaster && (
              <button
                data-testid="cf-sync-btn"
                type="button"
                onClick={syncStrategy}
                disabled={busy === "cf-sync"}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold border border-[#7DD3FC]/40 text-[#7DD3FC] hover:bg-[#7DD3FC]/10 disabled:opacity-40"
              >
                <RefreshCw size={10} className={busy === "cf-sync" ? "animate-spin" : ""} /> Sync Strategy
              </button>
            )}
            {isMaster && (
              <button
                data-testid="cf-resync-all-btn"
                type="button"
                onClick={resyncAllFollowers}
                disabled={busy === "cf-resync-all"}
                title="Push the latest symbol mapping to every existing follower (XM / Vantage / IC Markets need this)"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold border border-[#FBBF24]/40 text-[#FBBF24] hover:bg-[#FBBF24]/10 disabled:opacity-40"
              >
                <RefreshCw size={10} className={busy === "cf-resync-all" ? "animate-spin" : ""} /> Resync All Followers
              </button>
            )}
          </div>
        </div>
        {cfState?.strategy?.last_error && (
          <p className="p-2.5 rounded-md bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-300 mb-2" data-testid="cf-strategy-error">
            <AlertTriangle size={10} className="inline mr-1" /> {cfState.strategy.last_error.slice(0, 240)}
          </p>
        )}
        {cfState?.strategy?.synced_at && (
          <div className="text-[10px] text-zinc-500">
            Last synced <span className="font-mono text-zinc-300">{cfState.strategy.synced_at.slice(0, 16)}</span> · Master account <span className="font-mono text-zinc-300">{(cfState.strategy.master_account_id || "").slice(0, 8)}…</span>
          </div>
        )}
      </div>

      {/* Configure master form */}
      <div className="p-5 rounded-xl border border-white/10 bg-[#13131A]/60" data-testid="bridge-master-form">
        <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
          <Power size={12} className="text-[#7DD3FC]" /> Configure Master MT5
        </h4>

        {/* Mode switcher */}
        <div className="mb-4 inline-flex rounded-md border border-zinc-800 bg-zinc-950/40 p-0.5">
          <button
            type="button"
            data-testid="bridge-mode-credentials"
            onClick={() => setMode("credentials")}
            disabled={!isMaster}
            className={`px-3 py-1.5 rounded text-[11px] font-semibold transition-all ${
              mode === "credentials" ? "bg-[#7DD3FC] text-[#0A0A0C]" : "text-zinc-400 hover:text-white"
            } disabled:opacity-40`}
          >
            MT5 Credentials
          </button>
          <button
            type="button"
            data-testid="bridge-mode-bind"
            onClick={() => setMode("bind")}
            disabled={!isMaster}
            className={`px-3 py-1.5 rounded text-[11px] font-semibold transition-all ${
              mode === "bind" ? "bg-[#7DD3FC] text-[#0A0A0C]" : "text-zinc-400 hover:text-white"
            } disabled:opacity-40`}
          >
            Existing MetaApi Account
          </button>
        </div>

        {mode === "bind" ? (
          /* Bind to existing MetaApi account ID */
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">MetaApi Account ID</label>
              <input
                data-testid="bridge-metaapi-account-id"
                type="text"
                value={form.metaapi_account_id}
                onChange={(e) => setForm(f => ({ ...f, metaapi_account_id: e.target.value }))}
                disabled={!isMaster}
                placeholder="e.g. 718c63c4-3a5c-4c80-a19b-48ad3111cd65"
                className="mt-1 w-full px-3 py-2 rounded-md bg-zinc-950 border border-zinc-800 text-white text-sm font-mono outline-none focus:border-[#7DD3FC]/50 disabled:opacity-40"
              />
              <p className="mt-1.5 text-[10px] text-zinc-500 flex items-start gap-1">
                <HelpCircle size={10} className="mt-0.5 flex-shrink-0" />
                <span>Paste the UUID from your MetaApi dashboard (<a href="https://app.metaapi.cloud/accounts" target="_blank" rel="noreferrer" className="underline text-zinc-400">app.metaapi.cloud/accounts</a>). The account must already be DEPLOYED and CONNECTED to the broker. Login/server will auto-fill after save.</span>
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Broker (display only)" testid="bridge-master-broker"
                value={form.broker_name} onChange={(v) => setForm(f => ({ ...f, broker_name: v }))} disabled={!isMaster} placeholder="Exness / XM / etc" />
              <Field label="Nickname" testid="bridge-master-nickname"
                value={form.nickname} onChange={(v) => setForm(f => ({ ...f, nickname: v }))} disabled={!isMaster} />
            </div>
            {data?.bind_mode && data?.mt5_login && (
              <div className="p-3 rounded-md bg-emerald-500/5 border border-emerald-500/20 text-[11px]">
                <p className="text-emerald-200 font-semibold flex items-center gap-1.5"><CheckCircle2 size={12} /> Currently bound</p>
                <p className="text-zinc-400 mt-1 font-mono">Login {data.mt5_login} · {data.mt5_server}</p>
              </div>
            )}
          </div>
        ) : (
          /* Credentials mode (original) */
          <>
        {/* Broker preset quick-pick */}
        {presets.length > 0 && (
          <div className="mb-4">
            <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1.5 block">Pick your broker (auto-fills server format)</label>
            <div className="flex flex-wrap gap-1.5">
              {presets.map(p => (
                <button
                  key={p.key}
                  data-testid={`bridge-preset-${p.key}`}
                  type="button"
                  disabled={!isMaster}
                  onClick={() => selectPreset(p.key)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${
                    presetKey === p.key
                      ? "bg-[#7DD3FC]/15 border-[#7DD3FC]/40 text-[#7DD3FC]"
                      : "bg-zinc-950/40 border-zinc-800 text-zinc-400 hover:border-zinc-700"
                  } disabled:opacity-40`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {activePreset?.help && (
              <p className="mt-2 text-[10px] text-zinc-500 flex items-start gap-1">
                <HelpCircle size={10} className="mt-0.5 flex-shrink-0" />
                <span>{activePreset.help}</span>
              </p>
            )}
            {activePreset?.server_examples?.length > 0 && (
              <p className="mt-1 text-[10px] text-zinc-600">
                Example servers: {activePreset.server_examples.map((s, i) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, mt5_server: s }))}
                    className="font-mono text-zinc-400 hover:text-[#7DD3FC] ml-1 underline-offset-2 hover:underline"
                  >{s}{i < activePreset.server_examples.length - 1 ? "," : ""}</button>
                ))}
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="MT5 Login" testid="bridge-master-login"
            value={form.mt5_login} onChange={(v) => setForm(f => ({ ...f, mt5_login: v }))} disabled={!isMaster} mono placeholder="e.g. 87654321" />
          <Field label="MT5 Server" testid="bridge-master-server"
            value={form.mt5_server} onChange={(v) => setForm(f => ({ ...f, mt5_server: v }))} disabled={!isMaster} placeholder="e.g. Exness-MT5Trial9" mono />
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{data?.password_set ? "MT5 Password (re-enter to update)" : "MT5 Password"}</label>
            <div className="mt-1 relative">
              <input
                data-testid="bridge-master-password"
                type={showPwd ? "text" : "password"}
                value={form.mt5_password}
                onChange={(e) => setForm(f => ({ ...f, mt5_password: e.target.value }))}
                disabled={!isMaster}
                placeholder={data?.password_set ? "•••••••• (leave blank to keep)" : "Main (trading) password"}
                className="w-full pr-9 px-3 py-2 rounded-md bg-zinc-950 border border-zinc-800 text-white text-sm outline-none focus:border-[#7DD3FC]/50 disabled:opacity-40"
              />
              <button
                type="button"
                data-testid="bridge-master-password-toggle"
                onClick={() => setShowPwd(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <p className="mt-1.5 text-[10px] text-amber-300/80 flex items-start gap-1" data-testid="bridge-password-hint">
              <KeyRound size={10} className="mt-0.5 flex-shrink-0" />
              <span>Use your <span className="font-bold">Main (trading)</span> password. The <span className="font-bold">Investor</span> password is read-only and will be rejected by MetaApi.</span>
            </p>
          </div>
          <Field label="Broker (display only)" testid="bridge-master-broker"
            value={form.broker_name} onChange={(v) => setForm(f => ({ ...f, broker_name: v }))} disabled={!isMaster} placeholder="Exness / XM / etc" />
          <Field label="Nickname" testid="bridge-master-nickname"
            value={form.nickname} onChange={(v) => setForm(f => ({ ...f, nickname: v }))} disabled={!isMaster} />
        </div>
          </>
        )}

        {testResult && (
          <div data-testid="bridge-test-result" className={`mt-4 p-3 rounded-md border text-[11px] ${
            testResult.ok ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" : "bg-rose-500/10 border-rose-500/30 text-rose-200"
          }`}>
            {testResult.ok ? (
              <span className="flex items-center gap-1.5"><CheckCircle2 size={12} /> Verified · deploy={testResult.deploy_state || "—"} · broker={testResult.connection_state || "—"}</span>
            ) : (
              <div>
                <span className="flex items-center gap-1.5 font-semibold"><AlertTriangle size={12} /> {testResult.error || "Failed"}</span>
                {testResult.deploy_state && (
                  <div className="mt-1 text-rose-200/80">Last deploy state: <span className="font-mono">{testResult.deploy_state}</span></div>
                )}
              </div>
            )}
          </div>
        )}

        {isMaster && (
          <div className="flex justify-between gap-2 mt-4 flex-wrap">
            <div>
              {data?.mt5_login && (
                <button
                  data-testid="bridge-master-clear"
                  type="button"
                  onClick={clear}
                  disabled={!!busy}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 disabled:opacity-40"
                >
                  <Trash2 size={12} /> {busy === "clear" ? "Clearing…" : "Clear Master Configuration"}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {mode === "credentials" && (
                <button
                  data-testid="bridge-test-btn"
                  type="button"
                  onClick={test}
                  disabled={!!busy}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border border-[#7DD3FC]/40 text-[#7DD3FC] hover:bg-[#7DD3FC]/10 disabled:opacity-40"
                >
                  <Zap size={12} /> {busy === "test" ? "Testing… (up to 60s)" : "Test Connection"}
                </button>
              )}
              <button
                data-testid="bridge-master-save"
                onClick={save}
                disabled={!!busy}
                className="px-4 py-2 rounded-md bg-[#7DD3FC] hover:bg-[#BAE6FD] text-[#0A0A0C] text-xs font-semibold disabled:opacity-40"
              >
                {busy === "save" ? "Saving…" : (mode === "bind" ? "Bind Master Account" : "Save Master Configuration")}
              </button>
            </div>
          </div>
        )}
        {!isMaster && (
          <p className="mt-3 text-[11px] text-zinc-500">Read-only · Master Admin can edit these settings.</p>
        )}
      </div>

      {/* Followers list */}
      <div className="p-5 rounded-xl border border-white/10 bg-[#13131A]/60" data-testid="bridge-followers-section">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-bold text-white flex items-center gap-2">
            <Users size={12} className="text-[#7DD3FC]" /> Active Followers
          </h4>
          <span className="text-[10px] text-zinc-500">{followers?.total || 0} affiliate{(followers?.total || 0) === 1 ? "" : "s"} subscribed</span>
        </div>
        <div className="rounded-lg border border-white/5 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-white/10">
              <tr>
                <th className="px-3 py-2 text-left">ID</th>
                <th className="px-3 py-2 text-left">Affiliate</th>
                <th className="px-3 py-2 text-left">CENT MT5</th>
                <th className="px-3 py-2 text-left">Mode</th>
                <th className="px-3 py-2 text-right">Setting</th>
                <th className="px-3 py-2 text-left">Last Sync / Error</th>
                {isMaster && <th className="px-3 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {(!followers || followers.rows.length === 0) && (
                <tr><td colSpan={isMaster ? 7 : 6} className="px-3 py-6 text-center text-[11px] text-zinc-500">No followers yet — affiliates enable copy-trading on their CENT MT5.</td></tr>
              )}
              {(followers?.rows || []).map(r => (
                <tr key={r.user_id} className={`border-b border-white/5 ${r.last_error ? "bg-rose-500/5" : ""}`} data-testid={`bridge-follower-row-${r.user_id}`}>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-400">{r.display_id || "—"}</td>
                  <td className="px-3 py-2">
                    <div className="text-white text-[12px]">{r.name || "—"}</div>
                    <div className="text-[10px] text-zinc-500">{r.email}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-300">
                    #{r.mt5_login_id || "—"}<br/>
                    <span className="text-zinc-500">{r.broker_name || "—"} · {r.cent_status || "—"}</span>
                  </td>
                  <td className="px-3 py-2 text-[10px] text-[#7DD3FC] font-semibold uppercase">{(r.lot_mode || "").replace("_", " ")}</td>
                  <td className="px-3 py-2 text-right font-mono text-[10px] text-zinc-300">
                    {r.lot_mode === "fixed_lot" && `${r.fixed_lot} lot`}
                    {r.lot_mode === "pct_master" && `${r.pct_of_master}% of master`}
                    {r.lot_mode === "risk_pct" && `${r.risk_pct}% risk`}
                  </td>
                  <td className="px-3 py-2 text-[10px]">
                    {r.last_synced_at ? (
                      <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 size={9} /> {r.last_synced_at.slice(0, 16)}</span>
                    ) : r.last_error ? (
                      <span className="inline-flex items-center gap-1 text-rose-300"><AlertTriangle size={9} /> Provision failed</span>
                    ) : <span className="text-zinc-500">never</span>}
                    {r.last_error && (
                      <div data-testid={`bridge-follower-err-${r.user_id}`} className="text-rose-400 mt-1 text-[10px] max-w-[280px] break-words leading-relaxed">
                        {r.last_error.slice(0, 180)}{r.last_error.length > 180 ? "…" : ""}
                      </div>
                    )}
                  </td>
                  {isMaster && (
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex flex-col gap-1">
                        {(r.last_error || !r.last_synced_at) && (
                          <button
                            data-testid={`bridge-follower-retry-${r.user_id}`}
                            onClick={() => retry(r.user_id, r.name)}
                            disabled={busy === `retry-${r.user_id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 disabled:opacity-40"
                          >
                            <RefreshCw size={9} className={busy === `retry-${r.user_id}` ? "animate-spin" : ""} /> Retry
                          </button>
                        )}
                        <button
                          data-testid={`cf-resubscribe-${r.user_id}`}
                          onClick={() => resubscribeFollower(r.user_id, r.name)}
                          disabled={busy === `cf-sub-${r.user_id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold border border-[#FBBF24]/30 text-[#FBBF24] hover:bg-[#FBBF24]/10 disabled:opacity-40"
                        >
                          <Zap size={9} /> Re-subscribe
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, disabled, mono, type, placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</label>
      <input
        data-testid={testid}
        type={type || "text"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder || ""}
        className={`mt-1 w-full px-3 py-2 rounded-md bg-zinc-950 border border-zinc-800 text-white text-sm outline-none focus:border-[#7DD3FC]/50 disabled:opacity-40 ${mono ? "font-mono" : ""}`}
      />
    </div>
  );
}
